import React, { useEffect, useState } from 'react';
import { Modal, Spin, SwitchInput, TextInput } from '@wajeez-web/components/ui';
import { CommonModalProps } from '@wajeez-web/components/modals';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { ButtonHeadless } from '@wajeez-web/components/ui';
import { AiOutlinePlus } from 'react-icons/ai';
import { MdEdit } from 'react-icons/md';
import { TrashIcon } from '@heroicons/react/solid';
import {
  AnswersActions,
  QuestionsActions,
  useContentDispatch,
  useContentSelector,
} from '@wajeez-web/stores/content';
import { Server } from '@wajeez-web/core/api-base';

interface QuestionsModalProps extends CommonModalProps<Input> {}

export type Input = {
  questionId: number;
  text?: string;
  answers?: {
    id?: number;
    text?: string;
    isCorrect?: boolean;
    new?: boolean;
  }[];
};

export const QuestionsModal: React.FC<QuestionsModalProps> = ({
  isOpen,
  onClose,
  data,
  onSubmit,
  loading,
}) => {
  const contentDispatch = useContentDispatch();

  const [input, setInput] = useState<Input>({
    questionId: data?.questionId || 0,
    text: data?.text || '',
    answers: [],
  });
  const [addAnswer, setAddAnswer] = useState(true);
  const [addedAnswers, setAddedAnswers] = useState<
    | {
        id?: number;
        text?: string | undefined;
        isCorrect?: boolean | undefined;
        edit?: boolean;
        new?: boolean;
      }[]
    | undefined
  >([]);
  const [currentAnswer, setCurrentAnswer] = useState<
    { text: string; isCorrect: boolean } | undefined
  >();
  const [currentAnswerEdited, setCurrentAnswerEdited] = useState<
    { text: string; isCorrect: boolean } | undefined
  >();

  const answersByQuestionIds = useContentSelector(
    ({ answers }) => answers.lookups
  );

  const answersByQuestionId = answersByQuestionIds[data?.questionId!];

  useEffect(() => {
    if (data?.questionId) {
      contentDispatch(
        AnswersActions.getAnswersByQuestionIdAsync(data.questionId)
      );
    }
  }, [data?.questionId]);

  useEffect(() => {
    if (data?.questionId) {
      const answers = answersByQuestionId?.data?.map((a) => {
        return {
          id: a.id,
          text: a.text,
          isCorrect: a.isCorrect,
          new: false,
        };
      });

      setInput({
        ...input,
        answers,
      });

      setAddedAnswers(
        answers?.map((a) => {
          return {
            ...a,
            edit: false,
          };
        })
      );
    }
  }, [data?.questionId, answersByQuestionId?.data]);

  const renderAddedAnswers = () => {
    return addedAnswers?.map((a) => {
      if (!a.edit) {
        return (
          <div className="w-full flex justify-center items-center shadow rounded-2xl px-4 pt-2 pb-2">
            <div className="w-full">{a.text}</div>
            <div className="ms-6">
              <SwitchInput
                name={`is-correct-${a.text}`}
                value={a.isCorrect}
                label="Correct"
                onChange={(name, value) => {
                  if (value) {
                    const newAnswers = addedAnswers?.map((s) => {
                      if (s.text === a.text) {
                        return { ...s, isCorrect: value };
                      } else {
                        return { ...s, isCorrect: false };
                      }
                    });

                    setAddedAnswers(newAnswers);

                    setInput({ ...input, answers: newAnswers });
                  }
                }}
              />
            </div>
            <div
              className="ms-3 cursor-pointer"
              onClick={() => {
                if (!currentAnswerEdited) {
                  const newAddedAnswers = addedAnswers?.map((aa) => {
                    if (aa.id === a.id) {
                      return {
                        ...aa,
                        edit: true,
                      };
                    } else {
                      return aa;
                    }
                  });

                  setCurrentAnswerEdited({
                    text: a.text!,
                    isCorrect: a.isCorrect!,
                  });

                  setAddedAnswers(newAddedAnswers);
                }
              }}
            >
              <MdEdit className="text-[16px] text-primary" />
            </div>
            <div
              className="ms-3 cursor-pointer"
              onClick={() => {
                const newAddedAnswers = addedAnswers?.filter(
                  (s) => s.text !== a.text
                );

                setAddedAnswers(newAddedAnswers);

                setInput({ ...input, answers: newAddedAnswers });

                if (!a.new) {
                  contentDispatch(
                    AnswersActions.deleteAnswerAsync({ answerId: a.id })
                  );
                }
              }}
            >
              <TrashIcon className="w-4 h-4 text-red-500" />
            </div>
          </div>
        );
      } else {
        return (
          <div className="mt-2 shadow rounded-2xl px-4 pt-2 pb-4">
            <div className="flex justify-center items-end">
              <div className="w-full">
                <TextInput
                  label="Answer"
                  name="answer"
                  value={currentAnswerEdited?.text}
                  onChange={(name, value) => {
                    setCurrentAnswerEdited({
                      text: value,
                      isCorrect: a.isCorrect!,
                    });
                  }}
                  required={true}
                />
              </div>
              <div className="ms-10 w-[50px]">
                <ButtonHeadless
                  heightStyle="h-[28px]"
                  bgStyle="bg-primary"
                  borderStyle="border-none"
                  action={() => {
                    const newAddedAnswers = addedAnswers?.map((aa) => {
                      if (aa.id === a.id) {
                        return {
                          ...aa,
                          text: currentAnswerEdited?.text,
                          edit: false,
                        };
                      } else {
                        return aa;
                      }
                    });

                    input.answers &&
                      currentAnswerEdited &&
                      setInput({
                        ...input,
                        answers: newAddedAnswers,
                      });

                    if (input.answers && currentAnswerEdited?.text) {
                      setAddedAnswers(newAddedAnswers);

                      setCurrentAnswerEdited(undefined);
                    }
                  }}
                >
                  <div className="text-white">
                    <ReturnTranslatedText
                      text={`buttons.Save`}
                      origintext="Save"
                    />
                  </div>
                </ButtonHeadless>
              </div>
            </div>
          </div>
        );
      }
    });
  };

  return (
    <Modal
      title="Add Question"
      withScroll={true}
      open={isOpen}
      onClose={onClose}
      loading={loading}
      onSubmit={() => onSubmit && onSubmit({ ...input })}
      handleAlert={false}
      alertTarget="modal"
      fontClassName="font-[Tajawal]"
    >
      {answersByQuestionId?.status === 'loading' ? (
        <div className="h-[400px] flex justify-center items-center">
          <Spin />
        </div>
      ) : (
        <div className="space-y-10">
          <TextInput
            label="Question"
            name="question"
            value={input.text}
            onChange={(name, value) => setInput({ ...input, text: value })}
            required={true}
          />
          <div className="">
            <div className="flex justify-start items-center">
              <ReturnTranslatedText
                origintext={'Answers'}
                text={`general.Answers`}
              />
              <div className="ms-2 w-[28px]">
                <ButtonHeadless
                  heightStyle="h-[24px]"
                  bgStyle="bg-transparent"
                  borderStyle="border-none"
                  action={() => {
                    setAddAnswer(true);
                  }}
                >
                  <div className="text-black">
                    <AiOutlinePlus />
                  </div>
                </ButtonHeadless>
              </div>
            </div>
            {addAnswer && (
              <div className="mt-2 shadow rounded-2xl px-4 pt-2 pb-4">
                <div className="flex justify-center items-end">
                  <div className="w-full">
                    <TextInput
                      label="Answer"
                      name="answer"
                      value={currentAnswer?.text}
                      onChange={(name, value) => {
                        setCurrentAnswer({
                          text: value,
                          isCorrect: !input.answers?.length ? true : false,
                        });
                      }}
                      required={true}
                    />
                  </div>
                  <div className="ms-10 w-[50px]">
                    <ButtonHeadless
                      heightStyle="h-[28px]"
                      bgStyle="bg-primary"
                      borderStyle="border-none"
                      action={() => {
                        input.answers &&
                          currentAnswer &&
                          setInput({
                            ...input,
                            answers: [
                              ...input.answers,
                              { ...currentAnswer, new: true },
                            ],
                          });

                        if (input.answers && currentAnswer?.text) {
                          setAddedAnswers([
                            ...input.answers,
                            { ...currentAnswer, id: Date.now(), new: true },
                          ]);

                          setAddAnswer(false);

                          setCurrentAnswer({ text: '', isCorrect: false });
                        }
                      }}
                    >
                      <div className="text-white">
                        <ReturnTranslatedText
                          text={`buttons.Save`}
                          origintext="Save"
                        />
                      </div>
                    </ButtonHeadless>
                  </div>
                </div>
              </div>
            )}
            {addedAnswers?.length ? (
              <div className="mt-6 space-y-2">{renderAddedAnswers()}</div>
            ) : null}
          </div>
        </div>
      )}
    </Modal>
  );
};
